import React, { useContext, useEffect, useState } from "react";
import styles from "./view360Card.module.scss";
import {
    Button,
    Text,
    ComponentEventHandler,
    Flex,
    MenuButton,
    MenuProps,
    Segment,
    ArrowLeftIcon,
    MenuIcon,
    Divider,
    SearchIcon,
} from "@fluentui/react-northstar";
import Tabs from "tabs/Tabs";
import { constants } from "shared/constants";
import { Tab } from "shared/models/Tab";
import pages, { getEntityIdParamValue, getEntityTypeFromPath, routeParams } from "shared/routing/pages";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SegmentContent } from "shared/components/segmentContent/SegmentContent";
import { Search } from "./components/search/Search";
import { SearchEntityTypeEnum, View360CardTab } from "./models";
import { EntityDetails, SearchEntityTypeIcon } from "./components";
import { View360Context } from "./store";
import { Title } from "shared/components";
import { MsTeamsContext } from "msTeams/store";
import { AuthContext } from "authorization/AuthProvider";
import { GrantEnum } from "./models/GrantEnum";
import { Loader } from "ecom-teams-apps-core";
import pl from "shared/messages/pl";
import EmptyContent from "./components/emptyContent/EmptyContent";

export interface View360CardProps {
    activeTab?: number;
}

const View360Card: React.FC<View360CardProps> = (props) => {
    const view360Context = useContext(View360Context);
    const msTeamsContext = useContext(MsTeamsContext);
    const [activeTab, setActiveTab] = useState<number | undefined>();
    const [tabs, setTabs] = useState<Tab[]>();
    const navigation = useNavigate();
    const [isEntityDetailsLoading, showIsEntityDetailsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const params = useParams();
    const location = useLocation();
    const authContext = useContext(AuthContext);

    const isMobileView = window.innerWidth < constants.view360MobileDeviceWidthPx;
    const entityId = getEntityIdParamValue(params);
    const entityType = getEntityTypeFromPath(location.pathname);

    useEffect(() => {
        setTabs([]);
        setActiveTab(undefined);
        if (props.activeTab === undefined) {
            return;
        }

        if (entityId) {
            view360Context.selectEntityToPresent({
                entityId: Number(entityId),
                entityType: entityType,
            });
        }
    }, [entityId, entityType]);

    useEffect(() => {
        if (!view360Context.selectedEntity) {
            return;
        }
        showIsEntityDetailsLoading(true);
        handleEntityChanged();
    }, [view360Context.selectedEntity]);

    useEffect(() => {
        if (!view360Context.selectedEntityDetails) {
            return;
        }
        setAvailableCards();
    }, [props.activeTab, view360Context.selectedEntityDetails]);

    const handleEntityChanged = async () => {
        let result = await view360Context.loadEntityDetails();
        result.isFailure ? setErrorMessage(result.exception?.message) : setErrorMessage("");
        showIsEntityDetailsLoading(false);
        window.scrollTo(0, 0);
    };

    const setAvailableCards = () => {
        const availableCards = getAvailableCards();
        setTabs(availableCards);
        if (props.activeTab !== undefined) {
            const index = availableCards.findIndex((c) => c.key === props.activeTab);
            setActiveTab(index);
        } else {
            setActiveTab(undefined);
        }

        showIsEntityDetailsLoading(false);
    };

    const getAvailableCards = (): Tab[] => {
        const availableTabs: Tab[] = [];
        availableTabs.push(constants.view360CardTabs.generalInformation);
        if (
            entityType === SearchEntityTypeEnum.Intermediary &&
            authContext.isUserGrantedTo(GrantEnum.V360Employees) &&
            view360Context.selectedEntityDetails?.acl?.employees
        ) {
            availableTabs.push(constants.view360CardTabs.employees);
        }
        if (
            (authContext.isUserGrantedTo(GrantEnum.V360SalesDataEhm) ||
                authContext.isUserGrantedTo(GrantEnum.V360SalesDataEhz)) &&
            view360Context.selectedEntityDetails?.acl?.salesReports
        ) {
            availableTabs.push(constants.view360CardTabs.sales);
        }
        if (authContext.isUserGrantedTo(GrantEnum.V360Notes) && view360Context.selectedEntityDetails?.acl?.notes) {
            availableTabs.push(constants.view360CardTabs.notes);
        }
        return availableTabs;
    };

    const handleOnActiveIndexChange: ComponentEventHandler<MenuProps> = (_, data) => {
        const index = Number(data?.activeIndex ?? 0);
        navigateToTab(index);
    };

    const handleOnMenuItemClick = (index: number | undefined) => {
        navigateToTab(index ?? 0);
    };

    const navigateToTab = (index: number) => {
        const currentTab = tabs![index];
        let navigateTo = pages.view360.path;

        // smell fishy
        if (view360Context.selectedEntity?.entityType === SearchEntityTypeEnum.Intermediary) {
            switch (currentTab.key) {
                case View360CardTab.GENERAL_INFORMATION:
                    navigateTo = pages.view360GeneralInformationTabIntermediary.path;
                    break;
                case View360CardTab.EMPLOYEES:
                    navigateTo = pages.view360EmployeesListIntermediary.path;
                    break;
                case View360CardTab.SALES:
                    navigateTo = pages.view360SalesTabIntermediary.path;
                    break;
                case View360CardTab.NOTES:
                    navigateTo = pages.view360NotesTabIntermediary.path;
                    break;
                default:
                    break;
            }
        }
        if (view360Context.selectedEntity?.entityType === SearchEntityTypeEnum.SalesPerson) {
            switch (currentTab.key) {
                case View360CardTab.GENERAL_INFORMATION:
                    navigateTo = pages.view360GeneralInformationTabSalesPerson.path;
                    break;
                case View360CardTab.SALES:
                    navigateTo = pages.view360SalesTabSalesPerson.path;
                    break;
                case View360CardTab.NOTES:
                    navigateTo = pages.view360NotesTabSalesPerson.path;
                    break;
                default:
                    break;
            }
        }

        const entityId = view360Context.selectedEntity?.entityId?.toString();
        navigation(navigateTo.replace(routeParams.id, entityId ?? ""));
    };

    const renderLoader = () => {
        return (
            <SegmentContent>
                <div className={styles.loaderWrapper}>
                    <Loader text={pl.view360Card.getLoaderMessage} />
                </div>
            </SegmentContent>
        );
    };

    if (isEntityDetailsLoading) {
        return renderLoader();
    }

    if (errorMessage) {
        return <EmptyContent message={errorMessage} graphic="empty" />;
    }

    return (
        <Flex
            column
            className={isMobileView && activeTab !== undefined ? styles.mobileContainer : undefined}
            styles={({ theme: { siteVariables } }) => ({
                backgroundColor: siteVariables.colorScheme.default.background2,
            })}
        >
            <Flex
                styles={({ theme: { siteVariables } }) => ({
                    backgroundColor: siteVariables.colorScheme.default.background2,
                })}
                column
                className={styles.sticky}
            >
                {activeTab !== undefined && (
                    <Flex column style={{ marginBottom: 8 }}>
                        <Flex vAlign="center" gap="gap.small">
                            <Button
                                iconOnly={true}
                                icon={<ArrowLeftIcon size="small" />}
                                onClick={() => {
                                    const lastGeneralInfoUrl = view360Context.popLastGeneralInfoViewUrl();
                                    if (lastGeneralInfoUrl === null) {
                                        navigation(pages.view360.path);
                                        return;
                                    }
                                    navigation(lastGeneralInfoUrl);
                                    showIsEntityDetailsLoading(true);
                                }}
                            />
                            {isMobileView && (
                                <Button
                                    iconOnly={true}
                                    icon={<SearchIcon size="small" />}
                                    onClick={() => {
                                        navigation(pages.view360.path);
                                        view360Context.reset360ViewHistory();
                                        showIsEntityDetailsLoading(true);
                                    }}
                                />
                            )}
                            {!isMobileView && (
                                <Tabs
                                    additionalClassName={styles.tabs}
                                    activeTab={activeTab}
                                    activeIndex={activeTab}
                                    items={tabs!}
                                    onActiveIndexChange={handleOnActiveIndexChange}
                                />
                            )}
                            {isMobileView && (
                                <Text
                                    size="medium"
                                    content={getAvailableCards().find((c) => c.key === props.activeTab!)?.content}
                                />
                            )}
                            {isMobileView && (
                                <Flex.Item push>
                                    <MenuButton
                                        trigger={
                                            <Button icon={<MenuIcon size="large" />} text iconOnly title="Create" />
                                        }
                                        menu={tabs!}
                                        onMenuItemClick={(e, x) => handleOnMenuItemClick(x?.index)}
                                    />
                                </Flex.Item>
                            )}
                        </Flex>
                    </Flex>
                )}
                {activeTab !== undefined && view360Context.selectedEntity && (
                    <Segment className={styles.header}>
                        <Flex column>
                            <Title
                                size="small"
                                text={
                                    view360Context.selectedEntityDetails
                                        ? `${view360Context.selectedEntityDetails?.businessIdentifier} ${view360Context.selectedEntityDetails?.name}`
                                        : ""
                                }
                                icon={
                                    <SearchEntityTypeIcon
                                        additionalClassName={
                                            msTeamsContext.currentMsTeamsThemeName === "default"
                                                ? styles.defaultRow
                                                : styles.darkRow
                                        }
                                        entityType={view360Context.selectedEntity.entityType}
                                        size="large"
                                    />
                                }
                                isLoading={isEntityDetailsLoading}
                                isHeader={!isMobileView}
                                vAlign="center"
                                weight={isMobileView ? "semibold" : undefined}
                            />
                            {props.activeTab === View360CardTab.SALES && view360Context.cardHeaderSubtitle && (
                                <>
                                    <Divider />
                                    <Flex fill hAlign="end">
                                        <Text
                                            size="small"
                                            styles={({ theme: { siteVariables } }) => ({
                                                color: siteVariables.colorScheme.default.foreground5,
                                            })}
                                        >
                                            {view360Context.cardHeaderSubtitle}
                                        </Text>
                                    </Flex>
                                </>
                            )}
                        </Flex>
                    </Segment>
                )}
            </Flex>
            <div className={styles.card}>
                {activeTab === undefined && (
                    <SegmentContent>
                        <Search />
                    </SegmentContent>
                )}
                {tabs !== undefined &&
                    tabs.length >= 1 &&
                    activeTab !== undefined &&
                    view360Context.selectedEntity &&
                    view360Context.selectedEntityDetails && (
                        <EntityDetails
                            entityDetails={view360Context.selectedEntityDetails}
                            activeTab={tabs[activeTab].key as number}
                        />
                    )}
            </div>
        </Flex>
    );
};

export default View360Card;
